/**
 * Polling intervals for RTK Query and custom poll utilities.
 *
 * Centralized so future tuning is one edit instead of grepping for magic
 * numbers across the codebase. Each export documents the trade-off
 * (freshness vs. request volume) so the next reader knows why these
 * specific values.
 *
 * Every consumer must guard polling with the `isTestEnv` flag from
 * `lib/env.ts` — E2E and Storybook keep the network quiescent. See
 * `.claude/rules/web/e2e-testing.md` "Polling Disabled in Test Env".
 * A poll that waits for a terminal state goes through
 * `hooks/useTerminalPollingGate.ts`, which applies that guard itself
 * (`.claude/rules/web/rtk-query.md` → "Polling").
 */

/**
 * Polling cadence for the pay/exchange success page while the BFF waits
 * for the upstream payment/withdrawal to reach RECONCILED or CANCELLED.
 *
 * Trade-off: settle time is typically 30–60s, so 3s gives ~10–20 polls of
 * UI freshness before terminal state. Faster polling would dominate trace
 * volume; slower polling would make the success page feel sluggish during
 * the last few seconds before settlement completes.
 *
 * Used by: `providers/PricingProvider/usePricingPolling.ts`.
 */
export const PRICING_REQUEST_POLLING_INTERVAL_MS = 3_000;

/**
 * How long the pay/exchange success page keeps polling one pricing request
 * before it stops and offers "Check again".
 *
 * Trade-off: settlement typically lands in 30–60s, so 5 minutes covers a slow
 * settlement many times over while ending the 3s poll (~100 requests) for a
 * request that is stuck, or an empty read that never reaches a final state.
 *
 * Used by: `providers/PricingProvider/usePricingPolling.ts`.
 */
export const PRICING_REQUEST_POLLING_CAP_MS = 5 * 60_000;

/**
 * Polling cadence for the price ticker (FX rates) on dashboard, accounts,
 * and exchange pages.
 *
 * Trade-off: rates change continuously but the user sees an aggregate
 * indication, not a tick-by-tick stream. 24s keeps the displayed rate
 * within ~1 cent for major pairs without flooding the ticker endpoint.
 *
 * Used by: `providers/TickerProvider/TickerProvider.tsx`.
 */
export const PRICES_POLLING_INTERVAL_MS = 24_000;

/**
 * Polling cadence for account balances on dashboard, accounts, and
 * exchange pages.
 *
 * Trade-off: balances change on user action (settled payments, exchanges)
 * — RTK Query mutations seed the cache directly via `enhancedApi.ts`
 * `onQueryStarted`, so this poll is the "catch missed updates" net. The
 * 54.8s value is deliberately co-prime with the 24s ticker poll to spread
 * load over time.
 *
 * Used by: `hooks/useBalances.ts`.
 */
export const BALANCES_POLLING_INTERVAL_MS = 54_800;

/**
 * Polling cadence for the onboarding application status page (waits for
 * the application to transition out of NEW/STARTED into OPENED).
 *
 * Trade-off: state transitions are server-driven and infrequent (every
 * few seconds at most). 10s is generous and the page auto-redirects on
 * OPENED, so polling typically only runs 2–3 times.
 *
 * Used by: `app/[locale]/(protected)/onboarding/status/page.tsx`.
 */
export const ONBOARDING_STATUS_POLLING_INTERVAL_MS = 10_000;

/**
 * How long the onboarding status page keeps polling before it stops and
 * offers "Check again".
 *
 * Trade-off: a review can take 1–2 business days, so no cap would see it
 * through; an open tab would otherwise poll every 10s for as long as it stays
 * open. 15 minutes covers the automated transitions a customer might wait
 * for on the page, then leaves the next check to them.
 *
 * Used by: `app/[locale]/(protected)/onboarding/status/page.tsx`.
 */
export const ONBOARDING_STATUS_POLLING_CAP_MS = 15 * 60_000;
